// The phone dashboards for the BOOKING (ART-358), MANAGEMENT (ART-361) and LABEL (ART-360) lenses. On mobile these lenses always
// show ONE artist: the one viewed last, else the roster's first by name (`resolveRosterArtistId`), switched from
// the avatar sheet. There is no All-roster view and no breadcrumb on the phone; desktop is unchanged.
//
// Every figure comes from a read the desktop artist page already makes — the booking cockpit
// (`useBookingArtistDashboard`), the management heartbeat (`useRosterArtistDashboard`), the release card,
// Needs attention and the booking offers — so no number here is a second implementation of a roster aggregate.
// What those reads do not carry (pre-saves, a 90-day confirmed total, average ticket fill) renders the em dash,
// never a fabricated 0.
import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Icon, MenuButton, compactNumber, type ActionMenuItem } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useResolvedOrg, useRosterArtists } from "../../app/hooks/useRoster.js";
import { useNeedsAttention } from "../../app/hooks/useNeedsAttention.js";
import { useBookingArtistDashboard } from "../../app/hooks/useBookingArtistDashboard.js";
import { useBookingOffers } from "../../app/hooks/useBookingOffers.js";
import { useRosterArtistDashboard } from "../../app/hooks/useRosterArtistDashboard.js";
import { useArtistReleaseCard } from "../../app/hooks/useReleaseCards.js";
import { useShowRequests } from "../../app/hooks/useShowRequests.js";
import { TaskListRow } from "../tasks/TaskListRow.js";
import { useDirectoryPeople } from "../tasks/taskPeople.js";
import { asTaskSummary, taskRowsClassName } from "../tasks/taskRow.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import { OppCompactRow } from "../dashboard/OppCompactRow.js";
import { openRequests, opportunityRow, showRequestRow } from "../dashboard/oppCompact.js";
import { oppLeftFeed } from "../dashboard/oppDisplay.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "../booking/CalendarEventDetails.js";
import { ShowRequestDetails } from "../booking/ShowRequestDetails.js";
import { CardError, CardLoading } from "../mobile/home/mobileCard.js";
import { NewDealModal } from "./NewDealModal.js";
import { NewOfferModal } from "./NewOfferModal.js";
import { RosterDealModal, type RosterDealTarget } from "./RosterDealModal.js";
import { BookingShowSheet, type BookingSheetTarget } from "./BookingShowSheet.js";
import {
  attentionKindLabel, attentionPill, attentionTiming, attentionTitle, type AttentionItem, type AttentionPillTone,
} from "./needsAttentionCopy.js";
import { formatDay, offerDealLine } from "./rosterFormat.js";
import styles from "./RosterMobileArtistHome.module.scss";

type RosterTodosCard = components["schemas"]["RosterTodosCard"];
type TopCitiesBlock = components["schemas"]["TopCitiesBlock"];
type OpportunityDto = components["schemas"]["OpportunityDto"];
type RosterReleaseCard = components["schemas"]["RosterReleaseCard"];
type RosterLastRelease = components["schemas"]["RosterLastRelease"];

const DASH = "—";
type Tone = AttentionPillTone;

function usd(cents: number | null | undefined): string {
  if (cents == null) return DASH;
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ---- shared pieces -----------------------------------------------------------------------------------------

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={styles.pill} data-tone={tone}>{children}</span>;
}

/** The list card: a mono label row (label left, count/meta right), rows, an optional footer link. */
function ListCard({ label, meta, footer, children }: {
  label: string;
  meta?: ReactNode;
  footer?: { label: string; onClick: () => void } | null;
  children: ReactNode;
}) {
  return (
    <section className={styles.card} aria-label={label}>
      <header className={styles.labelRow}>
        <h2 className={styles.label}>{label}</h2>
        {meta != null && <span className={styles.meta}>{meta}</span>}
      </header>
      {children}
      {footer && (
        <button type="button" className={styles.footer} onClick={footer.onClick}>
          {footer.label} →
        </button>
      )}
    </section>
  );
}

function EmptyLine({ children }: { children: ReactNode }) {
  return <p className={styles.emptyLine}>{children}</p>;
}

/** The artist's display name, off the rail read the shell already made (cache-shared). */
function useArtistName(): string {
  const { bandId } = useBandContext();
  const { orgId } = useResolvedOrg();
  const rail = useRosterArtists(orgId);
  return rail.data?.find((a) => a.bandId === bandId)?.name || "Artist";
}

/** "Good morning," · FIRST NAME · "{Artist} · n things need you", with the page's one + (the create menu). */
function Greeting({ needCount, createItems }: { needCount: number; createItems: ActionMenuItem[] }) {
  const me = useMe();
  const artist = useArtistName();
  const person = me.data?.person;
  const name = person?.firstName || person?.displayName || "";
  const hour = new Date().getHours();
  const prefix = hour < 12 ? "Good morning," : hour < 18 ? "Good afternoon," : "Good evening,";
  const need = needCount === 0 ? null : needCount === 1 ? "1 thing needs you" : `${needCount} things need you`;
  return (
    <section className={styles.greeting} aria-label="Greeting">
      <div className={styles.greetText}>
        <p className={styles.greetPrefix}>{prefix}</p>
        <p className={styles.greetName}>{name.toUpperCase()}</p>
        <p className={styles.greetSub}>{[artist, need].filter(Boolean).join(" · ")}</p>
      </div>
      <MenuButton items={createItems} menuLabel="Create" variant="accent" size="sm" placement="below" className={styles.greetAdd}>
        <Icon icon={Plus} size={16} />
      </MenuButton>
    </section>
  );
}

/** "Hold · 2 of 3 in · expires in 4d" — the kind, the people, the clock. */
function attentionSub(item: AttentionItem): string {
  const invitees = item.invitees ?? [];
  const inCount = invitees.filter((p) => p.status === "in").length;
  const people = invitees.length > 0 ? `${inCount} of ${invitees.length} in` : null;
  const who = item.source?.name && item.kind !== "hold" ? `by ${item.source.name}` : null;
  return [attentionKindLabel(item), people, who, attentionTiming(item)?.text].filter(Boolean).join(" · ");
}

/** NEEDS ATTENTION — the server's items for this artist; four rows tall, then it scrolls inside the card. On the
 *  booking dashboard (`onOpenShow`) a show opens the phone show sheet (ART-358); elsewhere it opens the editor. */
function AttentionCard({ onOpenRelease, onOpenShow }: {
  onOpenRelease?: (releaseId: string) => void;
  onOpenShow?: (target: BookingSheetTarget) => void;
}) {
  const { bandId } = useBandContext();
  const q = useNeedsAttention();
  const [target, setTarget] = useState<RosterDealTarget | null>(null);
  const items = q.data?.items ?? [];
  const open = (item: AttentionItem): (() => void) | undefined => {
    if (item.kind === "request" && item.offerId) return () => setTarget({ kind: "request", bandId, offerId: item.offerId! });
    if (item.showId && ["hold", "show", "ready", "agent", "settle"].includes(item.kind ?? "")) {
      if (onOpenShow) {
        const stage = item.kind === "ready" ? "ready" : item.kind === "hold" ? "hold" : undefined;
        return () => onOpenShow({ kind: "show", showId: item.showId!, stage });
      }
      return () => setTarget({ kind: "show", bandId, showId: item.showId! });
    }
    if (item.kind === "release" && item.releaseId && onOpenRelease) return () => onOpenRelease(item.releaseId!);
    return undefined;
  };
  return (
    <ListCard label="Needs attention" meta={q.data ? (q.data.total ?? items.length) : null}>
      {q.isLoading ? (
        <CardLoading />
      ) : q.isError ? (
        <CardError error={q.error} onRetry={() => void q.refetch()} />
      ) : items.length === 0 ? (
        <EmptyLine>Nothing pressing</EmptyLine>
      ) : (
        <ul className={styles.scroll4}>
          {items.map((item) => {
            const pill = attentionPill(item);
            const onClick = open(item);
            const body = (
              <>
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{attentionTitle(item)}</span>
                  <span className={styles.rowSub}>{attentionSub(item)}</span>
                </span>
                {pill && <Pill tone={pill.tone}>{pill.text}</Pill>}
                {(item.invitees?.length ?? 0) > 0 ? (
                  <span className={styles.rings}>
                    {item.invitees!.slice(0, 4).map((p) => (
                      <span key={p.personId} className={styles.ring} data-status={p.status}>
                        <PersonAvatar personId={p.personId} avatarMediaId={p.avatarMediaId} initials={initials(p.name)} size="sm" />
                      </span>
                    ))}
                  </span>
                ) : item.source?.personId ? (
                  <PersonAvatar personId={item.source.personId} avatarMediaId={null} initials={initials(item.source.name)} size="sm" />
                ) : null}
              </>
            );
            return (
              <li key={item.id} className={styles.row}>
                {onClick ? (
                  <button type="button" className={styles.rowButton} onClick={onClick}>{body}</button>
                ) : (
                  <div className={styles.rowButton}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <RosterDealModal
        target={target}
        onClose={() => {
          setTarget(null);
          void q.refetch();
        }}
      />
    </ListCard>
  );
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] : "")).toUpperCase() || "?";
}

/** TO-DO — the app's one task row (ART-343) over the artist page's own To-do read. No + here: the greeting owns it. */
function TodoCard({ todos, onOpenTask }: { todos: RosterTodosCard | null | undefined; onOpenTask?: (taskId: string) => void }) {
  const people = useDirectoryPeople(todos?.people ?? undefined);
  const rows = todos?.items ?? [];
  if (rows.length === 0) return null;
  return (
    <ListCard label="To-do" meta={todos?.open ?? rows.length}>
      <ul className={taskRowsClassName(styles.taskRows)}>
        {rows.slice(0, 6).map((t, i) => (
          <TaskListRow
            key={t.taskId ?? i}
            task={asTaskSummary(t)}
            people={people}
            onOpen={t.taskId && onOpenTask ? () => onOpenTask(t.taskId!) : undefined}
          />
        ))}
      </ul>
    </ListCard>
  );
}

/** Open to-dos past their due date — half of the booking greeting's "n things need you". */
function overdueCount(todos: RosterTodosCard | null | undefined): number {
  const today = todayIso();
  return (todos?.items ?? []).filter(
    (t) => t.dueDate && t.dueDate.slice(0, 10) < today && t.status !== "done" && t.status !== "approved",
  ).length;
}

/** WHERE YOUR AUDIENCE LIVES — the top five markets, with the server's own verdict: a booked market states its
 *  date; a market the server says to BOOK says so; anything else carries no pill (Route/Warm are desktop's). */
function AudienceCard({ block }: { block: TopCitiesBlock | null | undefined }) {
  const [all, setAll] = useState(false);
  const cities = block?.cities ?? [];
  const total = cities.reduce((a, c) => a + (c.audience ?? 0), 0);
  const markets = block?.marketsTracked ?? cities.length;
  const shown = all ? cities : cities.slice(0, 5);
  return (
    <ListCard
      label="Where your audience lives"
      meta={cities.length > 0 ? compactNumber(total) : null}
      footer={!all && markets > 5 && cities.length > 5 ? { label: `See all ${markets} markets`, onClick: () => setAll(true) } : null}
    >
      {cities.length === 0 ? (
        <EmptyLine>No audience data yet. Markets appear once streaming syncs.</EmptyLine>
      ) : (
        <ul className={styles.list}>
          {shown.map((c, i) => (
            <li key={`${c.city}-${c.region}-${i}`} className={styles.row}>
              <span className={styles.rowButton}>
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>
                    {c.city} {c.region && <span className={styles.region}>{c.region}</span>}
                  </span>
                  <span className={styles.rowSub}>
                    {compactNumber(c.audience ?? 0)} listeners · {c.lastShowOn ? `last played ${formatDay(c.lastShowOn)}` : "never played"}
                  </span>
                </span>
                {c.nextShowOn ? (
                  <Pill tone="green">✓ {formatDay(c.nextShowOn)}</Pill>
                ) : c.action === "book" ? (
                  <Pill tone="red">Book</Pill>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </ListCard>
  );
}

// ---- BOOKING (ART-358) ---------------------------------------------------------------------------------------

type Stage = "offer" | "hold" | "ready";

interface DealRow {
  key: string;
  date: string | null | undefined;
  title: string;
  sub: string;
  amount: string | null;
  amountCents: number;
  onOpen: () => void;
}

export function BookingMobileDashboard({ onOpenTask }: { onOpenTask?: (taskId: string) => void }) {
  const { bandId } = useBandContext();
  const navigate = useNavigate();
  const canLifetime = useCapability("money:lifetime");
  const canOffers = useCapability("offers:manage");
  const q = useBookingArtistDashboard(canLifetime);
  const attention = useNeedsAttention();
  const offersQuery = useBookingOffers({ enabled: canOffers });
  const [stage, setStage] = useState<Stage>("hold");
  const [taskOpen, setTaskOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [sheet, setSheet] = useState<BookingSheetTarget | null>(null);

  const data = q.data;
  const createItems: ActionMenuItem[] = [
    canOffers ? { label: "New offer", onSelect: () => setOfferOpen(true) } : null,
    { label: "New task", onSelect: () => setTaskOpen(true) },
  ].filter((x): x is ActionMenuItem => x !== null);

  const needCount = (attention.data?.total ?? attention.data?.items?.length ?? 0) + overdueCount(data?.todoCard);

  // The three stages, from the same two sources the desktop board reads: open offers (the pre-hold shortlist),
  // then the live holds split by the server's `ready` verdict.
  const offers = (offersQuery.data?.offers ?? []).filter((o) => o.status !== "converted");
  const holds = data?.activeDeals ?? [];
  const byStage: Record<Stage, DealRow[]> = {
    offer: offers.map((o) => ({
      key: o.offerId ?? "",
      date: o.date,
      title: o.request?.venue?.name || o.buyerVenue || "TBD",
      sub: o.status === "soft_hold" ? "Soft hold" : o.status === "requested" ? "Waiting on artist" : "Passed by team",
      amount: offerDealLine(o.amountCents ?? null, o.dealType ?? "guarantee", o.doorSplitPct ?? null),
      amountCents: o.amountCents ?? 0,
      onOpen: () => setSheet({ kind: "offer", offer: o }),
    })),
    hold: holds.filter((h) => !h.ready).map((h) => holdRow(h)),
    ready: holds.filter((h) => h.ready).map((h) => holdRow(h)),
  };
  function holdRow(h: (typeof holds)[number]): DealRow {
    const invited = h.invitedCount ?? 0;
    return {
      key: h.showId ?? "",
      date: h.date,
      title: h.venueName || "TBD",
      sub: [h.city, invited > 0 ? `${h.inCount ?? 0} of ${invited} in` : null,
        h.holdExpiresOn ? `exp ${formatDay(h.holdExpiresOn)}` : null].filter(Boolean).join(" · "),
      amount: h.guaranteeCents != null ? usd(h.guaranteeCents) : null,
      amountCents: h.guaranteeCents ?? 0,
      onOpen: () => h.showId && setSheet({ kind: "show", showId: h.showId, stage: h.ready ? "ready" : "hold" }),
    };
  }
  const inPlay = [...byStage.offer, ...byStage.hold, ...byStage.ready].reduce((a, r) => a + r.amountCents, 0);
  const rows = byStage[stage];

  return (
    <div className={styles.home}>
      <Greeting needCount={needCount} createItems={createItems} />
      <AttentionCard onOpenShow={setSheet} />
      {!canLifetime ? null : q.isLoading ? (
        <section className={styles.card}><CardLoading /></section>
      ) : q.isError || !data ? (
        <section className={styles.card}><CardError error={q.error} onRetry={() => void q.refetch()} /></section>
      ) : (
        <>
          <Next60Card
            bookedCents={data.kpis?.confirmedGross60dCents}
            holdCents={data.kpis?.pipelineValueCents}
            shows={(data.routing ?? []).length}
            avgGuaranteeCents={data.kpis?.avgGuaranteeCents}
            lifetimeCents={data.kpis?.touringLifetimeCents}
          />
          <ListCard
            label="Active deals"
            meta={`${usd(inPlay)} in play`}
            footer={{ label: "Open calendar", onClick: () => void navigate({ to: `/roster/artists/${bandId}/calendar`, search: {} }) }}
          >
            <div className={styles.stages} role="tablist" aria-label="Deal stage">
              {(["offer", "hold", "ready"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={stage === s}
                  className={styles.stage}
                  data-active={stage === s || undefined}
                  onClick={() => setStage(s)}
                >
                  <span className={styles.stageCount}>{byStage[s].length}</span>
                  <span className={styles.stageLabel}>{s}</span>
                </button>
              ))}
            </div>
            {rows.length === 0 ? (
              <EmptyLine>{STAGE_EMPTY[stage]}</EmptyLine>
            ) : (
              <ul className={styles.scroll3}>
                {rows.map((r) => (
                  <li key={r.key} className={styles.row}>
                    <button type="button" className={styles.rowButton} onClick={r.onOpen}>
                      <DateBlock date={r.date} />
                      <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{r.title}</span>
                        {r.sub && <span className={styles.rowSub}>{r.sub}</span>}
                      </span>
                      {r.amount && <span className={styles.amount}>{r.amount}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ListCard>
          <TodoCard todos={data.todoCard} onOpenTask={onOpenTask} />
          <AudienceCard block={data.topCities} />
        </>
      )}

      {taskOpen && (
        <TaskCreateModal
          onClose={() => setTaskOpen(false)}
          onCreated={(taskId) => {
            setTaskOpen(false);
            void q.refetch();
            onOpenTask?.(taskId);
          }}
        />
      )}
      {offerOpen && <NewOfferModal onClose={() => setOfferOpen(false)} />}
      {sheet && (
        <BookingShowSheet
          target={sheet}
          onClose={() => {
            setSheet(null);
            void q.refetch();
            void attention.refetch();
            void offersQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

const STAGE_EMPTY: Record<Stage, string> = {
  offer: "No offers in play.",
  hold: "No holds right now.",
  ready: "Nothing ready to confirm yet.",
};

function DateBlock({ date }: { date: string | null | undefined }) {
  const d = date ? new Date(`${date.slice(0, 10)}T12:00:00`) : null;
  const ok = d && !Number.isNaN(d.getTime());
  return (
    <span className={styles.dateBlock} aria-hidden="true">
      <span className={styles.dateMonth}>{ok ? d!.toLocaleDateString(undefined, { month: "short" }) : "TBD"}</span>
      <span className={styles.dateDay}>{ok ? d!.getDate() : DASH}</span>
    </span>
  );
}

/** NEXT 60 DAYS — booked revenue against what is on hold, as one stacked bar. */
function Next60Card({ bookedCents, holdCents, shows, avgGuaranteeCents, lifetimeCents }: {
  bookedCents: number | null | undefined;
  holdCents: number | null | undefined;
  shows: number;
  avgGuaranteeCents: number | null | undefined;
  lifetimeCents: number | null | undefined;
}) {
  const booked = bookedCents ?? 0;
  const hold = holdCents ?? 0;
  const sum = booked + hold;
  const bookedPct = sum > 0 ? (booked / sum) * 100 : 0;
  return (
    <section className={styles.summary} aria-label="Next 60 days">
      <header className={styles.labelRow} data-flush>
        <h2 className={styles.label}>Next 60 days</h2>
        <span className={styles.metaStrong}>{bookedCents == null && holdCents == null ? DASH : usd(sum)} possible</span>
      </header>
      <p className={styles.bigLine}>
        <span className={styles.big}>{usd(bookedCents)}</span>
        <span className={styles.bigSub}>booked</span>
      </p>
      <div className={styles.bar} aria-hidden="true">
        {booked > 0 && <span className={styles.barBooked} style={{ flexBasis: `${bookedPct}%` }} />}
        {hold > 0 && <span className={styles.barHold} style={{ flexBasis: `${100 - bookedPct}%` }} />}
      </div>
      <p className={styles.legend}>
        <span><i className={styles.swatchBooked} /> {shows === 1 ? "1 show confirmed" : `${shows} shows confirmed`}</span>
        <span><i className={styles.swatchHold} /> {usd(holdCents)} on hold</span>
      </p>
      <p className={styles.summaryFoot}>
        <span>Avg guarantee <strong>{usd(avgGuaranteeCents)}</strong></span>
        <span>Lifetime <strong>{usd(lifetimeCents)}</strong></span>
      </p>
    </section>
  );
}

// ---- LABEL (ART-360) -----------------------------------------------------------------------------------------

/** The label's phone dashboard: the release card, Needs attention, Opportunities and To-do. Streaming, Marketing
 *  and Trending stay off the phone (Streaming has its own tab; the release card carries ad spend). */
export function LabelMobileDashboard({ onOpenTask, onOpenRelease }: {
  onOpenTask?: (taskId: string) => void;
  onOpenRelease: (releaseId: string) => void;
}) {
  const q = useRosterArtistDashboard();
  const releaseCard = useArtistReleaseCard();
  const attention = useNeedsAttention();
  const canOpp = useCapability("opportunities:create");
  const [taskOpen, setTaskOpen] = useState(false);
  const [oppOpen, setOppOpen] = useState(false);
  const data = q.data;
  const createItems: ActionMenuItem[] = [
    { label: "New task", onSelect: () => setTaskOpen(true) },
    canOpp ? { label: "New opportunity", onSelect: () => setOppOpen(true) } : null,
  ].filter((x): x is ActionMenuItem => x !== null);
  const needCount = attention.data?.total ?? attention.data?.items?.length ?? 0;

  return (
    <div className={styles.home}>
      <Greeting needCount={needCount} createItems={createItems} />
      {releaseCard.data?.card ? (
        <ReleaseCard card={releaseCard.data.card} onOpen={onOpenRelease} status="phase" />
      ) : releaseCard.data?.lastRelease ? (
        <LastReleaseCard release={releaseCard.data.lastRelease} onOpen={onOpenRelease} />
      ) : null}
      <AttentionCard onOpenRelease={onOpenRelease} />
      {q.isLoading ? (
        <section className={styles.card}><CardLoading /></section>
      ) : q.isError || !data ? (
        <section className={styles.card}><CardError error={q.error} onRetry={() => void q.refetch()} /></section>
      ) : (
        <>
          <OpportunitiesFeed opportunities={data.opportunities} />
          <TodoCard todos={data.todos} onOpenTask={onOpenTask} />
        </>
      )}

      {taskOpen && (
        <TaskCreateModal
          onClose={() => setTaskOpen(false)}
          onCreated={(taskId) => {
            setTaskOpen(false);
            void q.refetch();
            onOpenTask?.(taskId);
          }}
        />
      )}
      {oppOpen && <NewDealModal title="New opportunity" submitLabel="Create" onClose={() => setOppOpen(false)} />}
    </div>
  );
}

// ---- MANAGEMENT (ART-361) ------------------------------------------------------------------------------------

export function ManagementMobileDashboard({ onOpenTask, onOpenRelease, onOpenTab }: {
  onOpenTask?: (taskId: string) => void;
  onOpenRelease: (releaseId: string) => void;
  onOpenTab: (tabKey: string) => void;
}) {
  const q = useRosterArtistDashboard();
  const releaseCard = useArtistReleaseCard();
  const attention = useNeedsAttention();
  const canOpp = useCapability("opportunities:create");
  const [taskOpen, setTaskOpen] = useState(false);
  const [oppOpen, setOppOpen] = useState(false);
  const data = q.data;
  const createItems: ActionMenuItem[] = [
    { label: "New task", onSelect: () => setTaskOpen(true) },
    canOpp ? { label: "New opportunity", onSelect: () => setOppOpen(true) } : null,
  ].filter((x): x is ActionMenuItem => x !== null);
  const needCount = attention.data?.total ?? attention.data?.items?.length ?? 0;
  const heartbeat = data?.heartbeat;

  return (
    <div className={styles.home}>
      <Greeting needCount={needCount} createItems={createItems} />
      {releaseCard.data?.card ? (
        <ReleaseCard card={releaseCard.data.card} onOpen={onOpenRelease} />
      ) : releaseCard.data?.lastRelease ? (
        <LastReleaseCard release={releaseCard.data.lastRelease} onOpen={onOpenRelease} />
      ) : null}
      <AttentionCard onOpenRelease={onOpenRelease} />
      {q.isLoading ? (
        <section className={styles.card}><CardLoading /></section>
      ) : q.isError || !data ? (
        <section className={styles.card}><CardError error={q.error} onRetry={() => void q.refetch()} /></section>
      ) : (
        <>
          <OpportunitiesFeed opportunities={data.opportunities} />
          <TodoCard todos={data.todos} onOpenTask={onOpenTask} />
          {/* Money-bearing and gated whole: `heartbeat.touring` is null without money:lifetime, so no card at all. */}
          {heartbeat?.touring && (
            <ListCard
              label="Shows"
              meta={heartbeat.touring.guaranteesCents != null ? `${usd(heartbeat.touring.guaranteesCents)} YTD` : null}
              footer={{ label: "Open shows", onClick: () => onOpenTab("schedule") }}
            >
              <ul className={styles.list}>
                <ValueRow
                  title={heartbeat.nextShows?.[0]
                    ? [heartbeat.nextShows[0].venueName, formatDay(heartbeat.nextShows[0].date)].filter(Boolean).join(" · ")
                    : "No show booked"}
                  sub={heartbeat.nextShows?.[0]
                    ? `Next show · ${[heartbeat.nextShows[0].city, heartbeat.nextShows[0].state].filter(Boolean).join(" ")}`
                    : "Next show"}
                  value={DASH}
                />
                <ValueRow
                  title={`${heartbeat.confirmedUpcoming ?? 0} confirmed`}
                  sub="Upcoming"
                  value={DASH}
                />
                <ValueRow title="Avg ticket fill" sub="Last 5 shows" value={DASH} />
              </ul>
            </ListCard>
          )}
          <AudienceCard block={heartbeat?.topCities} />
        </>
      )}

      {taskOpen && (
        <TaskCreateModal
          onClose={() => setTaskOpen(false)}
          onCreated={(taskId) => {
            setTaskOpen(false);
            void q.refetch();
            onOpenTask?.(taskId);
          }}
        />
      )}
      {oppOpen && <NewDealModal title="New opportunity" submitLabel="Create" onClose={() => setOppOpen(false)} />}
    </div>
  );
}

function ValueRow({ title, sub, value }: { title: string; sub: string; value: string }) {
  return (
    <li className={styles.row}>
      <span className={styles.rowButton}>
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>{title}</span>
          <span className={styles.rowSub}>{sub}</span>
        </span>
        <span className={styles.amount}>{value}</span>
      </span>
    </li>
  );
}

/** The next upcoming release: cover, title, a status pill, four tiles and the rollout milestones. The pill is a
 *  countdown on management and the release PHASE on label (ART-360: PRE-RELEASE until release day, then IN CYCLE). */
function ReleaseCard({ card, onOpen, status = "countdown" }: {
  card: RosterReleaseCard;
  onOpen: (releaseId: string) => void;
  status?: "countdown" | "phase";
}) {
  const budget = card.budget;
  const days = card.daysToRelease;
  const total = card.assetsTotal ?? 0;
  const added = card.assetsAdded ?? 0;
  const steps = (card.timeline ?? []).slice(0, 4);
  return (
    <section className={styles.summary} aria-label="Release">
      <div className={styles.relHead}>
        <ReleaseArtwork
          size="tile"
          className={styles.relCover}
          seed={card.releaseId ?? card.title ?? ""}
          mediaId={card.coverMediaId}
          imageUrl={card.coverImageUrl}
          lazy={false}
        />
        <div className={styles.relText}>
          <h2 className={styles.relTitle}>{card.title || "Untitled"}</h2>
          <p className={styles.relMeta}>
            {[card.releaseType?.toUpperCase(), card.releaseDate ? formatDay(card.releaseDate).toUpperCase() : null]
              .filter(Boolean).join(" · ")}
          </p>
          {status === "phase"
            ? (card.upcoming && days != null && days > 0
              ? <Pill tone="amber">Pre-release</Pill>
              : <Pill tone="green">In cycle</Pill>)
            : card.upcoming && days != null
              ? <Pill tone="amber">{days <= 0 ? "Today" : `In ${days} day${days === 1 ? "" : "s"}`}</Pill>
              : <Pill tone="green">In cycle</Pill>}
        </div>
      </div>
      <div className={styles.tiles}>
        <Tile label="Assets in" value={total > 0 ? `${added} / ${total}` : DASH}>
          {total > 0 && (
            <span className={styles.progress} aria-hidden="true">
              <span style={{ inlineSize: `${Math.min(100, (added / total) * 100)}%` }} />
            </span>
          )}
        </Tile>
        <Tile
          label="Budget"
          value={budget?.totalCents != null ? usd(budget.totalCents) : DASH}
          sub={budget?.totalCents != null ? `${usd(budget.totalCents - (budget.spentCents ?? 0))} left` : null}
        />
        <Tile
          label="Ad spend"
          value={budget?.adSpendCents ? usd(budget.adSpendCents) : DASH}
          sub={budget ? `${budget.adCampaigns ?? 0} active ad${budget.adCampaigns === 1 ? "" : "s"}` : null}
        />
        <Tile label="Pre-saves" value={DASH} />
      </div>
      {steps.length > 0 && (
        <ol className={styles.milestones} aria-label="Rollout">
          {steps.map((s, i) => (
            <li key={`${s.date}-${i}`} className={styles.milestone} data-current={s.state === "next" || undefined} data-done={s.state === "done" || undefined}>
              <span className={styles.dot} />
              <span className={styles.msDate}>{s.date ? formatDay(s.date) : ""}</span>
              <span className={styles.msName}>{s.name}</span>
            </li>
          ))}
        </ol>
      )}
      {card.releaseId && (
        <button type="button" className={styles.footer} data-flush onClick={() => onOpen(card.releaseId!)}>
          Open release →
        </button>
      )}
    </section>
  );
}

/** No release ahead: the most recent one, in cycle. */
function LastReleaseCard({ release, onOpen }: { release: RosterLastRelease; onOpen: (releaseId: string) => void }) {
  return (
    <section className={styles.summary} aria-label="Release">
      <div className={styles.relHead}>
        <ReleaseArtwork
          size="tile"
          className={styles.relCover}
          seed={release.releaseId ?? release.title ?? ""}
          mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl}
          lazy={false}
        />
        <div className={styles.relText}>
          <h2 className={styles.relTitle}>{release.title || "Untitled"}</h2>
          <p className={styles.relMeta}>
            {[release.releaseType?.toUpperCase(), release.releaseDate ? formatDay(release.releaseDate).toUpperCase() : null]
              .filter(Boolean).join(" · ")}
          </p>
          <Pill tone="green">In cycle</Pill>
        </div>
      </div>
      {release.releaseId && (
        <button type="button" className={styles.footer} data-flush onClick={() => onOpen(release.releaseId!)}>
          Open release →
        </button>
      )}
    </section>
  );
}

function Tile({ label, value, sub, children }: { label: string; value: string; sub?: string | null; children?: ReactNode }) {
  return (
    <div className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
      {sub && <span className={styles.tileSub}>{sub}</span>}
      {children}
    </div>
  );
}

/** OPPORTUNITIES — the artist page's org-scoped feed (the desktop card's data), each its own card on the page. */
function OpportunitiesFeed({ opportunities }: { opportunities: readonly OpportunityDto[] | null | undefined }) {
  const myPersonId = useMe().data?.person?.personId;
  const decides = useCapability("booking:decide");
  const requests = openRequests(useShowRequests({ enabled: decides }).data?.requests);
  const items = (opportunities ?? []).filter((o) => !oppLeftFeed(o, myPersonId));
  const [editing, setEditing] = useState<OpportunityDto | null>(null);
  const [details, setDetails] = useState<OpenCalendarEvent | null>(null);
  const [request, setRequest] = useState<string | null>(null);
  const count = items.length + requests.length;
  if (count === 0) return null;
  return (
    <section className={styles.bare} aria-label="Opportunities">
      <h2 className={styles.bareLabel}>Opportunities · {count}</h2>
      <ul className={styles.oppFeed}>
        {requests.map((r) => (
          <OppCompactRow key={`request:${r.offerId}`} {...showRequestRow(r)} actionLabel="Details" onAction={() => setRequest(r.offerId!)} />
        ))}
        {items.map((o, i) => {
          const id = o.opportunityId ?? `opp-${i}`;
          const m = opportunityRow(o, myPersonId);
          return (
            <OppCompactRow
              key={id}
              {...m}
              actionLabel={m.side === "creator" ? "Edit" : "Details"}
              onAction={() => (m.side === "creator" ? setEditing(o) : setDetails({ kind: "opportunity", id, anchor: null }))}
            />
          );
        })}
      </ul>
      {editing && <NewDealModal opp={editing} title="Edit opportunity" onClose={() => setEditing(null)} />}
      {details && <CalendarEventDetails open={details} onClose={() => setDetails(null)} />}
      {request && <ShowRequestDetails offerId={request} onClose={() => setRequest(null)} />}
    </section>
  );
}

